import { pushRoute } from '../../lib/router.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { selectFlattenedEmployees } from '../../modules/employees.js';
import {
  createTask,
  deleteTask,
  retrieveTasks,
  selectRetrieveTasksState,
  selectTask,
  updateTask,
} from '../../modules/tasks.js';
import {
  retrieveChangeImpacts,
  selectChangeImpact,
  selectRetrieveChangeImpactsState,
} from '../../modules/changeImpacts.js';
import TaskHeader from '../../components/headers/PageHeaders/Project/Task/TaskHeader.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import TaskForm from '../../components/projects/ChangeImpact/TaskForm/index.jsx';
import {
  mapFinalFormErrors,
  momentToDateString,
  stringToMoment,
} from '../../lib/utils.js';
import {
  retrieveProject,
  selectProject,
  selectRetrieveProjectState,
} from '../../modules/projects.js';
import Empty from '../../components/Empty.jsx';
import EmployeeFormContainer from '../employees/EmployeeFormContainer.jsx';
import {
  getEditorSaveDataPromise,
  getReadyStatusPromise,
} from '../../components/editor/defaultOptions.js';

const mapErrors = mapFinalFormErrors('Failed to save project');

const mapStateToProps = (state, ownProps) => ({
  employees: selectFlattenedEmployees(state),
  task: selectTask(state, ownProps),
  project: selectProject(ownProps.projectId)(state),
  changeImpact: selectChangeImpact(ownProps.changeImpactId)(state),
  retrieveTasksSuccess: selectRetrieveTasksState(state).success,
  retrieveChangeImpactsSuccess: selectRetrieveChangeImpactsState(state).success,
  retrieveProjectSuccess: selectRetrieveProjectState(state).success,
});

const mapDispatchToProps = {
  createTask,
  updateTask,
  deleteTask,
  retrieveTasks,
  retrieveChangeImpacts,
  retrieveProject,
};

const TaskFormContainer = (props) => {
  const {
    slug,
    task,
    taskId,
    projectId,
    changeImpactId,
    project,
    changeImpact,
    employees,
    createTask,
    deleteTask,
    updateTask,
    retrieveTasks,
    retrieveChangeImpacts,
    retrieveProject,
    retrieveProjectSuccess,
    retrieveTasksSuccess,
    retrieveChangeImpactsSuccess,
  } = props;

  const editingQuery = {
    objectType: 'task',
    objectId: taskId,
    active: !taskId,
  };

  const initialValue = useMemo(
    () =>
      task && {
        ...task,
        startDate: stringToMoment(task.startDate),
        endDate: stringToMoment(task.endDate),
      },
    [retrieveTasksSuccess]
  );

  const [selectedOwners, setSelectedOwners] = useState([]);
  const [editorInstance, setEditorInstance] = useState(null);

  const [showEmployeeModal, setShowEmployeeModal] = useState(false);

  const onHideEmployeeModal = () => setShowEmployeeModal(false);
  const onShowEmployeeModal = () => setShowEmployeeModal(true);

  const onSelectOwner = (owner) => {
    const isSelected = selectedOwners.some((e) => e.id === owner.id);

    if (isSelected) {
      if (!window.confirm('Are you sure you want to detach this task owner?'))
        return;
      setSelectedOwners(selectedOwners.filter((item) => item.id !== owner.id));
    } else {
      setSelectedOwners([...selectedOwners, owner]);
    }
  };

  const onAddEmployeeSubmitSuccess = ({ entities, result }) => {
    onSelectOwner(entities.employees[result]);
  };

  const goBack = () =>
    pushRoute('projects/change-impacts/show', { projectId, changeImpactId });

  const onDeleteTask = useCallback(() => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    deleteTask(task.id).then(goBack);
  }, []);

  const onSubmit = (values) => {
    const composedData = (editorData) => ({
      ...values,
      employeeIds: selectedOwners.map((e) => e.id),
      startDate: momentToDateString(values.startDate),
      endDate: momentToDateString(values.endDate),
      description: JSON.stringify(editorData),
    });

    return getReadyStatusPromise(editorInstance).then(() =>
      getEditorSaveDataPromise(editorInstance).then((editorData) =>
        taskId
          ? updateTask(taskId, composedData(editorData))
              .then(goBack)
              .catch(mapErrors)
          : createTask(changeImpactId, composedData(editorData))
              .then(goBack)
              .catch(mapErrors)
      )
    );
  };

  const validate = useCallback((values) => {
    const errors = {};
    if (!values.title) {
      errors.title = 'Title is required';
    }
    if (!values.status) {
      errors.status = 'Status is required';
    }

    return errors;
  }, []);

  useEffect(() => {
    !retrieveChangeImpactsSuccess && retrieveChangeImpacts(projectId);
    !retrieveTasksSuccess && retrieveTasks(changeImpactId);
    !retrieveProjectSuccess && retrieveProject(projectId);
  }, []);

  useEffect(() => {
    if (task) {
      setSelectedOwners(task.owners);
    }
  }, [retrieveTasksSuccess]);

  return project && changeImpact && employees ? (
    <React.Fragment>
      <TaskHeader
        slug={slug}
        project={project}
        changeImpact={changeImpact}
        task={task}
      />
      <SubHeader flexJustify small>
        <h2>{task ? 'Edit' : 'Add'} Task</h2>

        {task ? (
          <button className="btn btn-danger" onClick={onDeleteTask}>
            Delete
          </button>
        ) : null}
      </SubHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    onSubmit={onSubmit}
                    initialValues={initialValue}
                    validate={validate}
                    employees={employees}
                    selectedOwners={selectedOwners}
                    onSelectOwner={onSelectOwner}
                    setEditorInstance={setEditorInstance}
                    onHideEmployeeModal={onHideEmployeeModal}
                    onShowEmployeeModal={onShowEmployeeModal}
                    additionalSearchParams={editingQuery}
                    onCancel={goBack}
                    component={TaskForm}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
      <EmployeeFormContainer
        showFormModal={showEmployeeModal}
        onHideFormModal={onHideEmployeeModal}
        onSubmitSuccess={onAddEmployeeSubmitSuccess}
      />
    </React.Fragment>
  ) : (
    <Empty simplified />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskFormContainer);
